import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Panel, SectionHeader } from "./DashboardPrimitives";
import { Invoice, Recipient } from "src/types";
import { TbSend, TbAt, TbPhone, TbCircleCheck, TbQuestionMark, TbChevronDown, TbClipboardCheck, TbCashBanknoteOff, TbCoins, TbBuildingBank, TbCopyCheck, TbShieldExclamation } from "react-icons/tb";
import { matchesInvoiceView } from "../Invoices/invoiceListViews";
import { isPostConsentBlocked } from "src/utils/invoiceSendability";
import { useApi } from "src/hooks/useApi";
import type { FinApiStatus } from "src/hooks/useFinApi";

const STATUS_OPEN = 0;

interface QuestionItem {
    id: string;
    recipientId: string;
    recipientName: string;
    invoiceId: string;
    invoiceReadableId: string;
    category: string;
}

interface DropdownEntry {
    key:       string;
    label:     string;
    sublabel?: string;
    badge?:    number;
    onClick:   () => void;
}

interface TodoItem {
    key:             string;
    icon:            React.ReactNode;
    accent:          string;
    count:           number;
    label:           string;
    onClick:         () => void;
    dropdownEntries: DropdownEntry[];
}

interface Props {
    invoices:                    Invoice[];
    recipients:                  Recipient[];
    unansweredQuestions?:        number;
    unansweredQuestionItems?:    QuestionItem[];
    questionnairePendingCount?:  number;
    /** Same gate Dashboard.tsx uses to show/hide FinApiPanel — a staff member without bank
     *  access shouldn't get a todo that just bounces them off /bank. */
    canSeeBank?:                 boolean;
    /** Same setting InvoiceRow.tsx gates on — kept in sync so this count and the row-level
     *  block/unblock state can never disagree on which invoices need consent. */
    requireLegalAcceptance?:     boolean;
}

function TodoDropdown({ entries, accent }: { entries: DropdownEntry[]; accent: string }) {
    return (
        <div className="todo-dropdown">
            {entries.map((entry) => (
                <button
                    key={entry.key}
                    className="todo-dropdown__item"
                    onClick={() => entry.onClick()}
                >
                    <span className="todo-dropdown__name">{entry.label}</span>
                    {entry.sublabel && (
                        <span className="todo-dropdown__sublabel">{entry.sublabel}</span>
                    )}
                    {entry.badge != null && (
                        <span className="todo-dropdown__badge" style={{ background: accent + "18", color: accent }}>
                            {entry.badge}
                        </span>
                    )}
                </button>
            ))}
        </div>
    );
}

function TodoRow({
    item,
    hasDropdown,
    isOpen,
    onClose,
}: {
    item:        TodoItem;
    hasDropdown: boolean;
    isOpen:      boolean;
    onClose:     () => void;
}) {
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) onClose();
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, [isOpen, onClose]);

    return (
        <div className="todo-row-wrap" ref={wrapRef} style={{ position: "relative" }}>
            <div className="todo-row" onClick={item.onClick}>
                <span
                    className="todo-row__icon"
                    style={{ color: item.accent, background: item.accent + "18" }}
                >
                    {item.icon}
                </span>
                <span className="todo-row__label">{item.label}</span>
                <div className="todo-row__right">
                    <span
                        className="todo-row__badge"
                        style={{ background: item.accent + "18", color: item.accent }}
                    >
                        {item.count}
                    </span>
                    {hasDropdown && (
                        <TbChevronDown
                            className={`todo-row__chevron${isOpen ? " todo-row__chevron--open" : ""}`}
                            style={{ color: item.accent }}
                        />
                    )}
                </div>
            </div>
            {hasDropdown && isOpen && (
                <TodoDropdown entries={item.dropdownEntries} accent={item.accent} />
            )}
        </div>
    );
}

export function TodoCard({ invoices, recipients, unansweredQuestions = 0, unansweredQuestionItems = [], questionnairePendingCount = 0, canSeeBank = false, requireLegalAcceptance = true }: Props) {
    const { t }    = useTranslation();
    const navigate = useNavigate();
    const [openKey, setOpenKey] = useState<string | null>(null);

    // Read-only, plain useApi (not the full useFinApi hook) on purpose — this card only
    // needs to count unmatched/double-matched transactions, not trigger useFinApi's own
    // auto-sync side effect (FinApiPanel elsewhere on this same dashboard already does that).
    const { data: bankStatus } = useApi<FinApiStatus>(canSeeBank ? "/banksapi/status" : "");
    // Every not-yet-matched transaction, not just old ones — a doctor sending a refund or
    // reconciling a payment is easy to forget either way, and surfacing it immediately gives
    // a chance to catch a mismatch before it goes stale.
    const unmatchedBankTxs = (bankStatus?.recentTransactions ?? [])
        .filter((tx) => !tx.ignored && (tx.matchedInvoiceIds?.length ?? 0) === 0);
    // Invoices matched to 2+ distinct bank transactions — a direct "this got recorded twice"
    // signal from the backend's own match records, independent of the amount-based overpaid
    // check below (see doubleMatchedInvoices in banksApiController.ts's getStatus).
    const doubleMatchedInvoices = bankStatus?.doubleMatchedInvoices ?? [];

    const toggle = (key: string) => setOpenKey((k) => (k === key ? null : key));

    const unsentInvoices = invoices.filter((inv) => inv.status === STATUS_OPEN && !inv.sentDate);
    // Subset of the above that's stuck specifically on missing consent. Gated on
    // postConsentAccepted alone (not questionnaireAccepted) regardless of postOnly: paper
    // Einwilligung consent never unlocks email, but it does unlock post, so once it's signed
    // the invoice is no longer stuck — it just goes out by post instead. postConsentAccepted
    // is already questionnaireAccepted-OR-postal-consent, so this still requires full digital
    // consent for a patient who hasn't signed anything at all.
    const consentBlockedInvoices = unsentInvoices.filter((inv) => isPostConsentBlocked(inv, requireLegalAcceptance));
    // Sent, past due, still unpaid — same definition as the Invoices page's "Overdue" view,
    // so this count and the filtered list it links to can never disagree. Not necessarily
    // unpaid in reality — some of these may already be paid but missed the bank auto-match,
    // hence the review-oriented label rather than an outright "unpaid" claim.
    const overdueUnmatchedInvoices = invoices.filter((inv) => matchesInvoiceView(inv, "overdue"));
    // Same disjoint-fields basis as InvoiceAmountsCard.tsx / InvoiceRow.tsx's overpaid banner —
    // kept in sync across all three so they never disagree on which invoices are overpaid.
    const overpaidInvoices = invoices.filter((inv) => {
        const vatRate = inv.vatRate ?? 0;
        const net     = (inv.amount ?? 0) + (inv.materialCosts ?? 0);
        const gross   = vatRate > 0 ? net * (1 + vatRate / 100) : net;
        const total   = gross + (inv.dunningFineAmount ?? 0);
        return (inv.paidAmount ?? 0) - total > 0.01;
    });
    const noEmailRecips  = recipients.filter((r) => !r.email);
    const noPhoneRecips  = recipients.filter((r) => !r.tel || r.tel.length === 0);

    const patientRowCount = (recips: Recipient[]) =>
        recips.reduce((sum, r) => {
            const pl = (r.patients ?? []).filter(Boolean);
            return sum + (pl.length > 1 ? pl.length : 1);
        }, 0);

    const noEmailCount = patientRowCount(noEmailRecips);
    const noPhoneCount = patientRowCount(noPhoneRecips);

    const questionEntries: DropdownEntry[] = unansweredQuestionItems.map((q) => ({
        key:    q.id,
        label:  q.recipientName || q.recipientId,
        sublabel: q.invoiceReadableId || undefined,
        onClick: q.invoiceId
            ? () => navigate(`/invoices/${q.invoiceId}`, { state: { tab: "questions" } })
            : () => navigate(`/patients/${q.recipientId}`, { state: { tab: "questionnaire" } }),
    }));

    const items: TodoItem[] = [
        {
            key:    "unsent",
            icon:   <TbSend />,
            accent: "#f59e0b",
            count:  unsentInvoices.length,
            label:  t("todos.unsentInvoices", { count: unsentInvoices.length }),
            onClick: () => {
                if (unsentInvoices.length === 1) navigate(`/invoices/${unsentInvoices[0].id}`);
                else toggle("unsent");
            },
            dropdownEntries: unsentInvoices.map((inv) => {
                const name = inv.patient
                    ? `${inv.patient.firstName ?? ""} ${inv.patient.lastName ?? ""}`.trim()
                    : inv.recipient
                    ? `${inv.recipient.firstName ?? ""} ${inv.recipient.lastName ?? ""}`.trim()
                    : inv.readableId;
                return {
                    key:      inv.id,
                    label:    name || inv.readableId,
                    sublabel: inv.readableId,
                    onClick:  () => navigate(`/invoices/${inv.id}`),
                };
            }),
        },
        {
            key:    "consentMissing",
            icon:   <TbShieldExclamation />,
            accent: "#b45309",
            count:  consentBlockedInvoices.length,
            label:  t("todos.consentMissing", { count: consentBlockedInvoices.length }),
            onClick: () => {
                if (consentBlockedInvoices.length === 1) navigate(`/invoices/${consentBlockedInvoices[0].id}`);
                else toggle("consentMissing");
            },
            dropdownEntries: consentBlockedInvoices.map((inv) => {
                const name = inv.patient
                    ? `${inv.patient.firstName ?? ""} ${inv.patient.lastName ?? ""}`.trim()
                    : inv.recipient
                    ? `${inv.recipient.firstName ?? ""} ${inv.recipient.lastName ?? ""}`.trim()
                    : inv.readableId;
                return {
                    key:      inv.id,
                    label:    name || inv.readableId,
                    sublabel: inv.readableId,
                    onClick:  () => navigate(`/invoices/${inv.id}`),
                };
            }),
        },
        {
            key:    "noEmail",
            icon:   <TbAt />,
            accent: "#6366f1",
            count:  noEmailCount,
            label:  t("todos.noEmail", { count: noEmailCount }),
            onClick: () => {
                if (noEmailRecips.length === 1) navigate(`/patients/${noEmailRecips[0].id}`);
                else toggle("noEmail");
            },
            dropdownEntries: noEmailRecips.map((r) => ({
                key:    r.id,
                label:  `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim() || r.id,
                onClick: () => navigate(`/patients/${r.id}`),
            })),
        },
        {
            key:    "noPhone",
            icon:   <TbPhone />,
            accent: "#14b8a6",
            count:  noPhoneCount,
            label:  t("todos.noPhone", { count: noPhoneCount }),
            onClick: () => {
                if (noPhoneRecips.length === 1) navigate(`/patients/${noPhoneRecips[0].id}`);
                else toggle("noPhone");
            },
            dropdownEntries: noPhoneRecips.map((r) => ({
                key:    r.id,
                label:  `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim() || r.id,
                onClick: () => navigate(`/patients/${r.id}`),
            })),
        },
        {
            key:     "questions",
            icon:    <TbQuestionMark />,
            accent:  "#8b5cf6",
            count:   unansweredQuestions,
            label:   t("todos.unansweredQuestions", { count: unansweredQuestions }),
            onClick: () => {
                if (questionEntries.length === 1) questionEntries[0].onClick();
                else toggle("questions");
            },
            dropdownEntries: questionEntries,
        },
        {
            key:    "questionnairePending",
            icon:   <TbClipboardCheck />,
            accent: "#0891b2",
            count:  questionnairePendingCount,
            label:  t("todos.questionnaireNoInvoice", { count: questionnairePendingCount }),
            onClick: () => navigate("/invoices", {
                state: { filters: { sent: "unsent", questionnaireSubmitted: true }, refresh: true },
            }),
            dropdownEntries: [],
        },
        {
            key:    "overdueUnmatched",
            icon:   <TbCashBanknoteOff />,
            accent: "#dc2626",
            count:  overdueUnmatchedInvoices.length,
            label:  t("todos.overdueUnmatched", { count: overdueUnmatchedInvoices.length }),
            onClick: () => navigate("/invoices", { state: { viewKey: "overdue" } }),
            dropdownEntries: [],
        },
        {
            key:    "unmatchedBankTx",
            icon:   <TbBuildingBank />,
            accent: "#0e7490",
            count:  unmatchedBankTxs.length,
            label:  t("todos.unmatchedBankTx", { count: unmatchedBankTxs.length }),
            onClick: () => navigate("/bank", { state: { statusTab: "unmatched" } }),
            dropdownEntries: [],
        },
        {
            key:    "doublePayments",
            icon:   <TbCopyCheck />,
            accent: "#b91c1c",
            count:  doubleMatchedInvoices.length,
            label:  t("todos.doublePayments", { count: doubleMatchedInvoices.length }),
            onClick: () => {
                if (doubleMatchedInvoices.length === 1) navigate("/bank", { state: { search: doubleMatchedInvoices[0].readableId } });
                else toggle("doublePayments");
            },
            dropdownEntries: doubleMatchedInvoices.map((inv) => ({
                key:      inv.id,
                label:    inv.patientName || inv.readableId,
                sublabel: `${inv.readableId} · ${inv.matchCount}× · €${inv.totalMatched.toFixed(2)}`,
                // Takes the doctor to the Bank page (where the duplicate match actually lives and
                // can be unmatched), not the invoice page — which has no bank-match data at all,
                // so this item could never actually be resolved from here before.
                onClick:  () => navigate("/bank", { state: { search: inv.readableId } }),
            })),
        },
        {
            key:    "overpaid",
            icon:   <TbCoins />,
            accent: "#1565c0",
            count:  overpaidInvoices.length,
            label:  t("todos.overpaidInvoices", { count: overpaidInvoices.length }),
            onClick: () => {
                if (overpaidInvoices.length === 1) navigate(`/invoices/${overpaidInvoices[0].id}`);
                else toggle("overpaid");
            },
            dropdownEntries: overpaidInvoices.map((inv) => {
                const name = inv.patient
                    ? `${inv.patient.firstName ?? ""} ${inv.patient.lastName ?? ""}`.trim()
                    : inv.recipient
                    ? `${inv.recipient.firstName ?? ""} ${inv.recipient.lastName ?? ""}`.trim()
                    : inv.readableId;
                return {
                    key:      inv.id,
                    label:    name || inv.readableId,
                    sublabel: inv.readableId,
                    onClick:  () => navigate(`/invoices/${inv.id}`),
                };
            }),
        },
    ].filter((item) => item.count > 0);

    return (
        <Panel>
            <SectionHeader title={t("todos.title")} />

            {items.length === 0 ? (
                <div className="todo-empty">
                    <span className="todo-empty__icon"><TbCircleCheck /></span>
                    <span>{t("todos.allDone")}</span>
                    <p className="todo-empty__hint">{t("todos.allDoneHint")}</p>
                </div>
            ) : (
                <div className="list-scroll-region">
                    <div className="todo-list">
                        {items.map((item) => (
                            <TodoRow
                                key={item.key}
                                item={item}
                                hasDropdown={item.dropdownEntries.length > 1}
                                isOpen={openKey === item.key}
                                onClose={() => setOpenKey(null)}
                            />
                        ))}
                    </div>
                </div>
            )}
        </Panel>
    );
}
